import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';

import {
  SubscriptionAdvisorModel,
  SubscriptionInvoiceResponseModel,
  SubscriptionPaymentProfileModel,
  SubscriptionProductModel,
  UpdateAdvisorSubscription,
  UpdatePaymentProfileModel,
} from '../../model';
import RequestCache from '../../utils/RequestCache';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  public readonly paymentProfile: RequestCache<SubscriptionPaymentProfileModel>;

  constructor(private http: HttpClient) {
    this.paymentProfile = new RequestCache(
      http.get<SubscriptionPaymentProfileModel>(`${environment.apiEndpoint}/account/subscription/payment-profile`),
    );
  }

  public getSubscriptionAdvisors(): Observable<SubscriptionAdvisorModel[]> {
    return this.http.get<SubscriptionAdvisorModel[]>(`${environment.apiEndpoint}/account/subscription/advisor`);
  }

  public getSubscriptionAdvisorById(advisorId: string): Observable<SubscriptionAdvisorModel> {
    return this.http.get<SubscriptionAdvisorModel>(`${environment.apiEndpoint}/account/advisor/${advisorId}`);
  }

  public createAdvisor(body: UpdateAdvisorSubscription): Observable<SubscriptionAdvisorModel> {
    return this.http.post<SubscriptionAdvisorModel>(`${environment.apiEndpoint}/account/advisor`, body);
  }

  public updateAdvisor(advisorId: string, body: UpdateAdvisorSubscription): Observable<void> {
    return this.http.put<void>(`${environment.apiEndpoint}/account/advisor/${advisorId}`, body);
  }

  public deleteAdvisor(advisorId: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiEndpoint}/account/advisor/${advisorId}`);
  }

  public reinviteAdvisor(advisorId: string): Observable<void> {
    return this.http.post<void>(`${environment.apiEndpoint}/account/advisor/${advisorId}/reinvite`, null);
  }

  public autoAllocateSeats(): Observable<any> {
    return this.http.post<any>(`${environment.apiEndpoint}account/subscription/auto-allocate-seats`, null);
  }

  public updatePaymentProfile(body: UpdatePaymentProfileModel): Observable<void> {
    return this.http
      .put<void>(`${environment.apiEndpoint}/account/subscription/payment-profile`, body)
      .pipe(tap(() => this.paymentProfile.clearCache()));
  }

  public getAddPaymentMethod(returnUrl: string): Observable<string> {
    return this.http.get(
      `${environment.apiEndpoint}/account/subscription/payment-method/add-url?return_url=${returnUrl}`,
      { responseType: 'text' },
    );
  }

  public deletePaymentMethod(paymentMethodId: string): Observable<void> {
    return this.http
      .delete<void>(`${environment.apiEndpoint}/account/subscription/payment-method/${paymentMethodId}`)
      .pipe(tap(() => this.paymentProfile.clearCache()));
  }

  public setDefaultPaymentMethod(paymentMethodId: string): Observable<void> {
    return this.http
      .put<void>(`${environment.apiEndpoint}/account/subscription/payment-method/default/${paymentMethodId}`, null)
      .pipe(tap(() => this.paymentProfile.clearCache()));
  }

  public getAvailableProducts(): Observable<SubscriptionProductModel[]> {
    return this.http.get<SubscriptionProductModel[]>(`${environment.apiEndpoint}/account/subscription/product`);
  }

  public getInvoices(limit: number, cursor?: string): Observable<SubscriptionInvoiceResponseModel> {
    const params = new HttpParams({
      fromObject: {
        limit: limit.toString(),
        ...(cursor && { cursor }),
      },
    });

    return this.http.get<SubscriptionInvoiceResponseModel>(`${environment.apiEndpoint}/account/subscription/invoice`, {
      params,
    });
  }
}
